import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import { LayoutGroup } from 'framer-motion';
import type { FunctionComponent } from 'react';
import { Fragment, Suspense, useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router';
import { useMediaQuery } from 'usehooks-ts';

import { AiContextProvider } from '~/shared/ai-context';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { useConsentQuery } from '~/shared/query-hooks/consent';
import { Tile } from '~/ui/core/tile/Tile';
import { ANIMATION_DURATION, LARGE_SCREEN_BREAKPOINT, MAIN_TILE_ID } from '~/ui/domain/tiling-engine/constants';
import { TilingEngine } from '~/ui/domain/tiling-engine/TilingEngine';
import { useTilingEngineContext } from '~/ui/domain/tiling-engine/TilingEngineContext';
import type { TileErrorBoundaryComponent } from '~/ui/domain/tiling-engine/types';
import { theme } from '~/ui/theme/cobrainer-theme';

import { AnnouncementsProvider } from '../announcements/AnnouncementsContext';
import {
    CHAT_TILE_ID,
    ChatBody,
    ChatComposerProvider,
    ChatSlotRenderersProvider,
    useChatOpen,
    useChatWidth,
} from '../chat';
import { PageLoadingSpinner } from '../design/components/LoadingSpinner';
import { ConfirmProvider } from '../job-architecture-v2/providers/ConfirmProvider';
import { TileAvailabilityProvider } from '../url-tiles/availability/tile-availability-context';
import { UrlTileAvailabilityCleanup } from '../url-tiles/availability/UrlTileAvailabilityCleanup';
import { useAvailableTileDefinitions } from '../url-tiles/availability/use-available-tile-definitions';
import { useUnavailableTilePayloadWarning } from '../url-tiles/availability/use-unavailable-tile-payload-warning';
import {
    renderUrlTileChrome,
    UrlTileRegistryProvider,
    urlTileDefinitions,
    urlTileLegacyRewriters,
    urlTilePermissionResolvers,
} from '../url-tiles/registry';
import { UrlTileNavigationProvider } from '../url-tiles/tile-navigation';
import { UrlTileLegacyRedirect } from '../url-tiles/UrlTileLegacyRedirect';
import { UrlTileProvider } from '../url-tiles/UrlTileProvider';
import { UrlTileRenderer } from '../url-tiles/UrlTileRenderer';
import { AutomaticAiContextCollector } from './ai-context/AutomaticAiContextCollector';
import { AppBar } from './app-bar/AppBar';
import {
    NAV_WIDTH_TRANSITION_DURATION_MS,
    NavigationMenuProvider,
    useNavigationMenu,
} from './app-bar/menu-bar-v2/NavigationMenuContext';
import {
    COLLAPSED_NAV_WIDTH_PX,
    EXPANDED_NAV_WIDTH_PX,
    SideDrawerNavigationV2,
} from './app-bar/menu-bar-v2/NavigationMenuV2';
import { shouldRenderOnboarding, shouldSuppressAnnouncements } from './app-base-conditions';
import { appChatSlotRenderers } from './chat-slot-registry';
import { LayoutSlotsProvider, useLayoutSlotRefs } from './common/layout-slots/LayoutSlots';
import { ConsentDialog } from './consent-dialog/ConsentDialog';
import { PageErrorBoundary, TileErrorBoundary } from './error-handling/ErrorBoundaries';
import { AppDrawers } from './pages/app-drawers';
import { route as onboardingRoutes } from './pages/onboarding/constants';
import { OnboardingForwarder } from './pages/onboarding/OnboardingForwarder';
import { ProfilePreview } from './pages/profile/profile-preview';
import { appTileRoutePolicies } from './tile-route-policies';
import { usePageLoadLatch } from './use-page-load-latch';

const tileErrorBoundary: TileErrorBoundaryComponent = ({ children, tileId }) => (
    <TileErrorBoundary tileId={tileId}>{children}</TileErrorBoundary>
);

const LayoutGrid = styled.div<{ readonly shouldOverflow: boolean }>(({ shouldOverflow }) => ({
    backgroundColor: theme.colors.gray[100],
    display: 'grid',
    flex: 1,
    gridTemplateColumns: shouldOverflow
        ? `${COLLAPSED_NAV_WIDTH_PX}px calc(100vw - ${COLLAPSED_NAV_WIDTH_PX}px)`
        : 'auto 1fr',
    overflowX: 'hidden',
}));

const HOVER_NAV_OFFSET_PX = EXPANDED_NAV_WIDTH_PX - COLLAPSED_NAV_WIDTH_PX;

const ContentShift = styled.div<{ readonly isShifted: boolean }>(({ isShifted }) => ({
    minWidth: 0,
    transform: isShifted ? `translateX(${HOVER_NAV_OFFSET_PX}px)` : 'translateX(0)',
    transition: `transform ${NAV_WIDTH_TRANSITION_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`,
    willChange: 'transform',
}));

export type LayoutVariant = 'canvas' | 'classic' | 'full-bleed';

const FullBleedContent = styled.div({
    height: '100%',
    minWidth: 0,
    overflow: 'auto',
});

type LayoutProps = {
    readonly hasNavigationMenu?: boolean;
    readonly shouldShowFeedbackAction?: boolean;
    readonly variant?: LayoutVariant;
};

const APP_BAR_HEIGHT_PX = 50;
const TILING_SURFACE_CONTENT_PADDING = '4px 8px 8px 0';

type ContentWrapperProps = {
    readonly variant: LayoutVariant;
    readonly tilingEnabled: boolean;
};

const AUTO_COLLAPSE_NAV_DELAY_MS = ANIMATION_DURATION * 1000;

const renderOutletContent = (variant: LayoutVariant) =>
    variant === 'full-bleed' ? (
        <Outlet />
    ) : (
        <Tile className="overflow-hidden">
            <div className="overflow-y-auto w-full" style={{ scrollbarWidth: 'thin' }}>
                <Outlet />
            </div>
        </Tile>
    );

const TilingContent: FunctionComponent<{ readonly variant: LayoutVariant }> = ({ variant }) => {
    const { setTopEl } = useLayoutSlotRefs();
    const isOpen = useChatOpen();
    const chatWidth = useChatWidth();

    const outlet = renderOutletContent(variant);

    return (
        <FullBleedContent>
            <div
                className="grid"
                style={{
                    gridTemplateRows: 'min-content minmax(0, 1fr)',
                    height: `calc(100vh - ${APP_BAR_HEIGHT_PX}px)`,
                }}
            >
                <div ref={setTopEl} />
                <LayoutGroup>
                    <TilingEngine.Surface
                        offsetTop={0}
                        style={{ height: 'auto', minHeight: 0, padding: TILING_SURFACE_CONTENT_PADDING }}
                        tileErrorBoundary={tileErrorBoundary}
                    >
                        <TilingEngine.Main minWidth="50%">{outlet}</TilingEngine.Main>
                        {isOpen && (
                            <TilingEngine.RightDock id={CHAT_TILE_ID} minWidth={chatWidth} width={chatWidth}>
                                <ThemeProvider theme={theme}>
                                    <ChatBody />
                                </ThemeProvider>
                            </TilingEngine.RightDock>
                        )}
                    </TilingEngine.Surface>
                    <UrlTileRegistryProvider>
                        <UrlTileRenderer renderTileChrome={renderUrlTileChrome} />
                    </UrlTileRegistryProvider>
                </LayoutGroup>
            </div>
        </FullBleedContent>
    );
};

// Canvas pages own their tile placement: the page renders the url tiles itself
// (e.g. skills-chat scroll dock), so no tiling surface and no UrlTileRenderer here.
const CanvasContent: FunctionComponent = () => {
    const { setTopEl } = useLayoutSlotRefs();

    return (
        <FullBleedContent>
            <div
                className="grid"
                style={{
                    gridTemplateRows: 'min-content minmax(0, 1fr)',
                    height: `calc(100vh - ${APP_BAR_HEIGHT_PX}px)`,
                }}
            >
                <div ref={setTopEl} />
                <Outlet />
            </div>
        </FullBleedContent>
    );
};

const NonTilingContent: FunctionComponent<{ readonly variant: LayoutVariant }> = ({ variant }) => {
    const { setTopEl } = useLayoutSlotRefs();

    return (
        <FullBleedContent>
            <div className="flex flex-col" style={{ height: `calc(100vh - ${APP_BAR_HEIGHT_PX}px)` }}>
                <div ref={setTopEl} />
                <div className="flex" style={{ flex: 1, height: 'auto', minHeight: 0, minWidth: 0 }}>
                    {renderOutletContent(variant)}
                </div>
            </div>
        </FullBleedContent>
    );
};

const ContentWrapper: FunctionComponent<ContentWrapperProps> = ({ variant, tilingEnabled }) => {
    if (variant === 'canvas') {
        return <CanvasContent />;
    }

    return tilingEnabled ? <TilingContent variant={variant} /> : <NonTilingContent variant={variant} />;
};

const AutoCollapseNavigationOnTilingExpansion: FunctionComponent = () => {
    const { closeMenu, isOpen } = useNavigationMenu();
    const { tileOrder } = useTilingEngineContext();
    const isLargeScreen = useMediaQuery(LARGE_SCREEN_BREAKPOINT);
    const previousPanelCountRef = useRef(Math.max(0, tileOrder.filter((id) => id !== MAIN_TILE_ID).length));
    const hasObservedTileOrderRef = useRef(false);

    useEffect(() => {
        const panelCount = tileOrder.filter((id) => id !== MAIN_TILE_ID).length;

        if (!hasObservedTileOrderRef.current && tileOrder.length > 0) {
            hasObservedTileOrderRef.current = true;
            previousPanelCountRef.current = panelCount;
            return;
        }

        const previousPanelCount = previousPanelCountRef.current;
        previousPanelCountRef.current = panelCount;

        if (!isOpen || isLargeScreen || previousPanelCount > 0 || panelCount === 0) {
            return;
        }

        const timeoutId = globalThis.setTimeout(() => {
            closeMenu();
        }, AUTO_COLLAPSE_NAV_DELAY_MS);

        return () => {
            globalThis.clearTimeout(timeoutId);
        };
    }, [closeMenu, isLargeScreen, isOpen, tileOrder]);

    return null;
};

const LayoutContent: FunctionComponent<LayoutProps> = ({
    hasNavigationMenu = true,
    shouldShowFeedbackAction = true,
    variant = 'classic',
}) => {
    const { applicationState } = useApplicationContext();
    const { locationSearchParams, myUserProfile } = applicationState;
    const { data: consentData, isSuccess: isConsentResolved } = useConsentQuery();
    const { pathname } = applicationState.location;
    const { isHovered, isOpen, isOverlayOpen, shouldContentOverflow } = useNavigationMenu();
    const isOnboardingPath = pathname.startsWith(onboardingRoutes.patterns.onboarding);
    const isOnboardingDialogOpen = locationSearchParams.has(onboardingRoutes.searchParams.onboardingStep);
    const userProfileExists = myUserProfile.mapOr(false, (profile) => profile.exists === true);

    const {
        availabilityByType,
        declaredTypes,
        definitions: availableTileDefinitions,
    } = useAvailableTileDefinitions({
        definitions: urlTileDefinitions,
        policies: appTileRoutePolicies,
    });
    const onUnavailableTilePayload = useUnavailableTilePayloadWarning(declaredTypes);
    const tileAvailability = useMemo(
        () => ({ availabilityByType, permissionResolvers: urlTilePermissionResolvers }),
        [availabilityByType],
    );

    const suppressAnnouncementsForPageLoad = usePageLoadLatch(
        shouldSuppressAnnouncements({
            consentStatus: consentData?.status,
            isConsentResolved,
            isOnboardingActive: isOnboardingPath || isOnboardingDialogOpen,
            isUserProfileResolved: myUserProfile.isJust,
            userProfileExists,
        }),
    );

    if (myUserProfile.isNothing) {
        return <PageLoadingSpinner />;
    }

    if (
        shouldRenderOnboarding({
            consentStatus: consentData?.status,
            onboardingRoutesPath: onboardingRoutes.patterns.onboarding,
            pathname,
            userProfileExists,
        })
    ) {
        return (
            <Fragment>
                <OnboardingForwarder />
                <ConsentDialog />
            </Fragment>
        );
    }

    const isAiChatEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('ai-chat');

    const contentCore = (
        <LayoutSlotsProvider>
            <AppBar />
            <Box component="main" flex={1} sx={{ display: 'flex', position: 'relative' }}>
                <PageErrorBoundary>
                    <Suspense fallback={<PageLoadingSpinner />}>
                        <LayoutGrid shouldOverflow={shouldContentOverflow}>
                            {hasNavigationMenu ? (
                                <SideDrawerNavigationV2 shouldShowFeedbackAction={shouldShowFeedbackAction} />
                            ) : (
                                <span />
                            )}
                            <ContentShift isShifted={!isOpen && (isHovered || isOverlayOpen)}>
                                <ContentWrapper variant={variant} tilingEnabled={!isOnboardingPath} />
                            </ContentShift>
                        </LayoutGrid>
                    </Suspense>
                    <AppDrawers />
                    <ProfilePreview />
                </PageErrorBoundary>
            </Box>
        </LayoutSlotsProvider>
    );

    const wrappedContent = isOnboardingPath ? (
        contentCore
    ) : (
        <TilingEngine.Provider>
            <UrlTileProvider definitions={availableTileDefinitions} onUnavailableTilePayload={onUnavailableTilePayload}>
                <UrlTileNavigationProvider>
                    <TileAvailabilityProvider value={tileAvailability}>
                        <UrlTileLegacyRedirect
                            definitions={availableTileDefinitions}
                            legacyRewriters={urlTileLegacyRewriters}
                        />
                        <UrlTileAvailabilityCleanup
                            allDefinitions={urlTileDefinitions}
                            availableDefinitions={availableTileDefinitions}
                            declaredTypes={declaredTypes}
                        />
                        {hasNavigationMenu ? <AutoCollapseNavigationOnTilingExpansion /> : null}
                        <ChatSlotRenderersProvider renderers={appChatSlotRenderers}>
                            <ChatComposerProvider isEnabled={isAiChatEnabled}>{contentCore}</ChatComposerProvider>
                        </ChatSlotRenderersProvider>
                    </TileAvailabilityProvider>
                </UrlTileNavigationProvider>
            </UrlTileProvider>
        </TilingEngine.Provider>
    );

    return (
        <Fragment>
            {isAiChatEnabled ? <AutomaticAiContextCollector /> : null}
            <AnnouncementsProvider suppressed={suppressAnnouncementsForPageLoad}>
                <ConfirmProvider>{wrappedContent}</ConfirmProvider>
            </AnnouncementsProvider>
        </Fragment>
    );
};

export const Layout: FunctionComponent<LayoutProps> = ({
    hasNavigationMenu = true,
    shouldShowFeedbackAction = true,
    variant = 'classic',
}) => (
    <AiContextProvider>
        <NavigationMenuProvider>
            <LayoutContent
                hasNavigationMenu={hasNavigationMenu}
                shouldShowFeedbackAction={shouldShowFeedbackAction}
                variant={variant}
            />
        </NavigationMenuProvider>
    </AiContextProvider>
);
